import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Phone, Lock, ArrowRight, UserPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import API, { formatApiError } from "@/lib/api";
import { SITE } from "@/constants/site";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sign, setSign] = useState({ name: "", phone: "", email: "", reason: "" });
  const [signBusy, setSignBusy] = useState(false);
  const [signDone, setSignDone] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(identifier.trim(), password);
      toast.success(t("log_welcome"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    if (signBusy) return;
    setSignBusy(true);
    try {
      await API.post("/signup-requests", sign);
      setSignDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSignBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/40 outline-none focus:border-[#F2B134]/70 transition-colors";

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10 relative overflow-hidden bg-[#120507]">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <img src="/hero.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#120507]/60 to-[#120507]" />
      </div>

      <div className="relative w-full max-w-md rounded-3xl border border-[#F2B134]/25 bg-[#1E0A0E]/90 backdrop-blur p-8 sm:p-10">
        <div className="mx-auto h-16 w-16 rounded-2xl overflow-hidden bg-[#FDF6EA] border border-[#F2B134]/40">
          <img src="/logo.jpg" alt="লোগো" className="w-full h-full object-cover" />
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2 rounded-full border border-[#F2B134]/25 p-1">
          <button
            type="button"
            data-testid="login-tab-btn"
            onClick={() => setTab("login")}
            className={`rounded-full py-2 text-sm font-bold transition-all ${tab === "login" ? "btn-gold" : "text-[#D1BBA7] hover:text-[#F2B134]"}`}
          >
            {t("tab_login")}
          </button>
          <button
            type="button"
            data-testid="signup-tab-btn"
            onClick={() => setTab("signup")}
            className={`rounded-full py-2 text-sm font-bold transition-all ${tab === "signup" ? "btn-gold" : "text-[#D1BBA7] hover:text-[#F2B134]"}`}
          >
            {t("tab_signup")}
          </button>
        </div>

        {tab === "login" ? (
          <form onSubmit={submit} data-testid="login-form">
            <h1 className="mt-6 text-center font-bengali font-extrabold text-2xl">
              {t("log_title").split(" ")[0]} <span className="gold-text">{t("log_title").split(" ").slice(1).join(" ")}</span>
            </h1>
            <p className="mt-2 text-center text-xs text-[#D1BBA7]/70">{t("log_sub")}</p>

            <label className="block mt-7 text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">{t("log_id_label")}</label>
            <div className="relative mt-2">
              <Phone size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D1BBA7]/50" />
              <input
                required
                autoFocus
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t("log_id_ph")}
                className={`${inputCls} pl-11`}
              />
            </div>

            <label className="block mt-5 text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">{t("log_pw_label")}</label>
            <div className="relative mt-2">
              <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D1BBA7]/50" />
              <input
                required
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={`${inputCls} pl-11`}
              />
            </div>

            <button type="submit" disabled={busy} data-testid="login-submit-btn" className="btn-gold mt-7 w-full rounded-xl py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? t("log_busy") : <>{t("log_submit")} <ArrowRight size={16} /></>}
            </button>

            <p className="mt-5 text-center text-xs text-[#D1BBA7]/70 font-bengali">{t("log_forgot")}</p>
          </form>
        ) : signDone ? (
          <div className="mt-6 text-center" data-testid="signup-success">
            <CheckCircle2 size={40} className="mx-auto text-[#F2B134]" />
            <h2 className="mt-4 font-bengali font-extrabold text-2xl gold-text">{t("sign_success_t")}</h2>
            <p className="mt-3 text-sm text-[#D1BBA7] font-bengali leading-relaxed">{t("sign_success_s")}</p>
          </div>
        ) : (
          <form onSubmit={submitSignup} data-testid="signup-form">
            <h1 className="mt-6 text-center font-bengali font-extrabold text-2xl">
              {t("sign_title").split(" ")[0]} <span className="gold-text">{t("sign_title").split(" ").slice(1).join(" ")}</span>
            </h1>
            <p className="mt-2 text-center text-xs text-[#D1BBA7]/70">{t("sign_sub")}</p>

            <div className="mt-7 space-y-4">
              <div className="relative">
                <UserPlus size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D1BBA7]/50" />
                <input required data-testid="signup-name-input" value={sign.name} onChange={(e) => setSign({ ...sign, name: e.target.value })} placeholder={t("sign_name")} className={`${inputCls} pl-11 font-bengali`} />
              </div>
              <div className="relative">
                <Phone size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D1BBA7]/50" />
                <input required inputMode="numeric" data-testid="signup-phone-input" value={sign.phone} onChange={(e) => setSign({ ...sign, phone: e.target.value })} placeholder={t("sign_phone")} className={`${inputCls} pl-11`} />
              </div>
              <input data-testid="signup-email-input" value={sign.email} onChange={(e) => setSign({ ...sign, email: e.target.value })} placeholder={t("sign_email")} className={inputCls} />
              <textarea rows={3} data-testid="signup-reason-input" value={sign.reason} onChange={(e) => setSign({ ...sign, reason: e.target.value })} placeholder={t("sign_reason")} className={`${inputCls} resize-none font-bengali`} />
            </div>

            <button type="submit" disabled={signBusy} data-testid="signup-submit-btn" className="btn-gold mt-7 w-full rounded-xl py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {signBusy ? t("sign_busy") : t("sign_submit")}
            </button>
          </form>
        )}

        <button type="button" onClick={() => navigate("/")} data-testid="login-back-btn" className="mt-4 w-full text-center text-xs text-[#D1BBA7]/60 hover:text-[#F2B134] transition-colors">
          {t("log_back")}
        </button>
      </div>
    </div>
  );
}
